import _ from 'lodash';
import { BellIcon, ChevronLeftIcon, MenuIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { twMerge } from 'tailwind-merge';
import firebaseStore from '@client/stores/firebaseStore';
import frameStore from '@client/stores/frameStore';
import historyStore from '@client/stores/historyStore';
import modalStore from '@client/stores/modalStore';
import notificationStore from '@client/stores/notificationStore';
import webViewStore from '@client/stores/webViewStore';
import { useAutorun } from '@client/clientUtils/mobxUtil';
import { CommonLink, useCommonNavigate } from '@client/component/CommonLink';
import HaewaoIcon from './common/icons/HaewaoIcon';
import sidebarStore from '@client/stores/sidebarStore';

const Navbar = observer(() => {
    const location = useLocation();
    const { navigate, navigateBack } = useCommonNavigate();
    const isRootMenu = ['/', '/news', '/portfolio', '/more'].includes(location.pathname);
    const isNotificationPage = location.pathname === '/notification';

    // 일정 수준 이상 스크롤 되었을 때 타이틀을 보여줌
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = _.debounce(() => {
            setIsScrolled(window.scrollY > 56);
        }, 100);

        window.addEventListener('scroll', handleScroll, {
            passive: true,
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // 읽지 않은 알림 체크
    useAutorun(() => {
        if (!firebaseStore.isInitialized) {
            return;
        }

        (async () => {
            const isLoggedIn = await firebaseStore.isLoggedIn();

            if (isLoggedIn) {
                try {
                    await notificationStore.checkHasUnreadNotifications();
                } catch (error) {
                    console.error('Failed to check unread notifications:', error);
                }
            }
        })();
    }, []);

    return (
        <div className="bg-base-100 border-base-content/10 fixed top-0 right-0 left-0 z-(--z-index-navbar) flex h-14 flex-row items-center justify-center border-b">
            <div
                className={twMerge(
                    'relative flex h-full max-w-(--main-max-width) grow items-center justify-between gap-1 px-2',
                )}
            >
                <div className="flex-none">
                    {/* 웹뷰 */}
                    {webViewStore.isWebView && (
                        <>
                            {/* 웹뷰 루트 메뉴에선 기본적으로 상단에 로고 */}
                            {isRootMenu && (
                                <CommonLink
                                    to={'/'}
                                    className="hidden-when-desktop btn btn-ghost btn-square btn-sm ml-2 block"
                                    preventScrollReset={false}
                                >
                                    <HaewaoIcon className="size-full" />
                                </CommonLink>
                            )}
                            {/* 웹뷰 루트 메뉴가 아닐 때, 뒤로 가기 버튼을 보여줌 */}
                            {!isRootMenu && (
                                <button
                                    className={twMerge(
                                        'hidden-when-desktop',
                                        'btn btn-square btn-ghost',
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();

                                        // go back or home
                                        if (historyStore.hasHistory()) {
                                            historyStore.pop();
                                            navigateBack();
                                        } else {
                                            if (webViewStore.isWebView) {
                                                webViewStore.sendMessageToApp('closeWebView');
                                            } else {
                                                navigate('/');
                                            }
                                        }
                                    }}
                                >
                                    <ChevronLeftIcon className="size-6" />
                                </button>
                            )}
                        </>
                    )}
                    {!webViewStore.isWebView && (
                        <>
                            {/* 웹뷰가 아닐 땐 모바일 사이즈에선 항상 메뉴 버튼을 보여줌 */}
                            <button
                                className={twMerge(
                                    'hidden-when-desktop',
                                    'btn btn-square btn-ghost',
                                )}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (sidebarStore.isOpen) {
                                        sidebarStore.closeSidebar();
                                    } else {
                                        sidebarStore.openSidebar();
                                    }
                                }}
                            >
                                <MenuIcon className="size-6" />
                            </button>
                        </>
                    )}
                </div>
                <div className="flex-auto overflow-hidden">
                    <div
                        className={twMerge(
                            'truncate text-base font-medium tracking-tighter transition-opacity',
                            isScrolled ? 'opacity-100' : 'opacity-0',
                        )}
                    >
                        {frameStore.currentTitle}
                    </div>
                </div>
                <div className="flex flex-none flex-row items-center">
                    {/* <div className="mr-1 flex items-center">
                        <CurrencyToggle />
                    </div> */}
                    {/* 알림 버튼 (알림 페이지에서는 보이지 않음) */}
                    {!isNotificationPage && (
                        <div className="mr-1 flex items-center">
                            <button
                                className="btn btn-ghost btn-square btn-sm"
                                onClick={async (e) => {
                                    e.preventDefault();

                                    if (firebaseStore.isInitialized) {
                                        const isLoggedIn = await firebaseStore.isLoggedIn();

                                        if (isLoggedIn) {
                                            navigate('/notification');
                                        } else {
                                            const { returnValue } = await modalStore.login();
                                            if (returnValue) {
                                                navigate('/notification');
                                            }
                                        }
                                    }
                                }}
                            >
                                <div className="indicator size-5">
                                    <BellIcon className="size-5" />
                                    {notificationStore.hasUnreadNotifications && (
                                        <span className="indicator-item bg-error size-1.5 rounded-full"></span>
                                    )}
                                </div>
                            </button>
                        </div>
                    )}
                    {/* 화면 클 때 보이는 로고 */}
                    <div
                        className={twMerge(
                            'shown-when-desktop',
                            'absolute top-0 -left-(--floating-sidebar-width) hidden h-full w-(--floating-sidebar-width) px-4',
                        )}
                    >
                        <div className="flex h-full items-center">
                            <CommonLink
                                to={'/'}
                                className="btn btn-ghost btn-square btn-sm"
                                preventScrollReset={false}
                            >
                                <HaewaoIcon className="size-full" />
                            </CommonLink>
                        </div>
                    </div>
                </div>
                {import.meta.env.DEV && (
                    <div
                        className="pointer-events-none absolute top-0.5 right-0.5 font-bold text-red-400"
                        style={{
                            fontSize: '10px',
                        }}
                    >
                        DEV
                    </div>
                )}
            </div>
        </div>
    );
});

export default Navbar;
